// M-249: Canonical interest taxonomy. Lives in `@planacle/shared` so that
// `User.interests` can be narrowed to `Partial<Record<CategoryKey, string[]>>`
// — a category rename anywhere in the codebase is now a TypeScript error
// instead of silently orphaning data on round-trip.

export const CATEGORIZED_ACTIVITIES = {
  'Food & Drink': [
    'Fine Dining',
    'Street Food',
    'Brunch',
    'Tapas',
    'Sushi',
    'Steakhouse',
    'Vegan/Veg',
    'Gastropub',
    'Sunday Roast',
    'Bottomless Brunch',
    'Street Food Market',
    'Craft Beer',
    'Cocktails',
    'Wine Tasting',
    'Speakeasies',
    'Rooftop Bars',
    'Pubs',
    'Coffee',
    'Bubble Tea',
    'Cooking Class',
    'Baking',
    'Chocolate Making',
    'Afternoon Tea',
    'Food Festivals',
  ],
  'Arts & Culture': [
    'Museums',
    'Galleries',
    'Science Museums',
    'Interactive Exhibits',
    'Musicals',
    'Plays',
    'Opera',
    'Ballet',
    'Comedy',
    'Poetry',
    'Photography',
    'Street Art',
    'Painting',
    'Sculpture',
    'History Tours',
    'Castles',
    'Cinema',
    'Circus',
    'Magic',
    'Aquarium',
  ],
  'Music & Nightlife': [
    'Concerts',
    'Intimate Gigs',
    'Jazz Clubs',
    'Classical',
    'Open Mic',
    'Festivals',
    'Nightclubs',
    'Karaoke',
    'Cabaret',
    'Drag Shows',
    'Silent Disco',
    'Immersive Experience',
    'Axe Throwing',
    'Crazy Golf',
    'Bingo',
    'Bowling',
    'Casino',
  ],
  'Outdoor & Active': [
    'Hiking',
    'Gardens',
    'Picnic',
    'Bird Watching',
    'Stargazing',
    'Kayaking',
    'Paddleboarding',
    'Swimming',
    'Wild Swimming',
    'Rock Climbing',
    'Go-Karting',
    'Paintball',
    'Escape Rooms',
    'Yoga',
    'Pilates',
    'Running',
    'Cycling',
    'Zoo',
  ],
  Sports: [
    'Football Match',
    'Basketball',
    'Rugby',
    '5-a-side',
    'Golf',
    'Mini Golf',
    'Padel',
    'Bouldering',
    'Badminton',
    'Squash',
  ],
  'Learning & Hobbies': [
    'Pottery',
    'Candle Making',
    'Floristry',
    'Woodworking',
    'Jewelry Making',
    'Book Clubs',
    'Pub Quiz',
    'Language Exchange',
    'Board Games',
    'Arcade',
    'VR',
    'eSports',
  ],
  'Shopping & Lifestyle': [
    'Vintage Shopping',
    'Farmers Markets',
    'Flea Markets',
    'Spa',
    'Meditation',
    'Sauna',
  ],
  Travel: ['Beach', 'Vineyards'],
} as const;

/** Category names as a string literal union — keys of `CATEGORIZED_ACTIVITIES`. */
export type CategoryKey = keyof typeof CATEGORIZED_ACTIVITIES;

/** Flat list of every activity across categories. */
export const ALL_ACTIVITIES: string[] = Object.values(CATEGORIZED_ACTIVITIES).flat();

/**
 * Flatten a user's grouped interests map into a single deduped string array.
 * Stable replacement for the inline `Object.values(interests).flat()` pattern
 * — callsites get a typed contract and we have one place to add normalisation
 * (dedupe, lowercase, etc.) if downstream needs change.
 */
export const flattenInterests = (
  interests: Partial<Record<CategoryKey, string[]>> | undefined
): string[] => {
  if (!interests) return [];
  const flat: string[] = [];
  for (const value of Object.values(interests)) {
    if (Array.isArray(value)) flat.push(...value);
  }
  return flat;
};
